import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { DownloadFile } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'

import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { getDpaeDisplayStatus } from '../../utils/functions/get-dpae-status'
import LabelWithText from './label-with-text'

type Props = {
  id: number
  endpoint: string
  dpaeDownloadAt?: string
  dpaeNumber?: string
  dpaeFilledAt?: string
  /** Foreign (english) staff are not hired under french law, so they have no DPAE */
  notApplicable?: boolean
  onDownloadDpaeXml?: () => void
}

export function DPAESection({
  id,
  endpoint,
  dpaeDownloadAt,
  dpaeNumber,
  dpaeFilledAt,
  notApplicable = false,
  onDownloadDpaeXml
}: Props) {
  const { downloadDocuments } = useDownloadDocuments()
  const dpaeDetails = getDpaeDisplayStatus(dpaeDownloadAt, dpaeFilledAt)

  const handleDownload = async () => {
    await downloadDocuments({ ids: [id], endpoint })
    onDownloadDpaeXml?.()
  }

  return (
    <InnerBox className="rounded-lg flex flex-col gap-3">
      <div className="flex items-center justify-between w-full gap-2">
        <div className="flex items-center gap-2">
          <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
            DPAE
          </Typography>
          {notApplicable ? (
            <Badge text={'N/A'} variant={'grey'} />
          ) : (
            <Badge text={dpaeDetails.label} variant={dpaeDetails.variant} />
          )}
        </div>

        {!notApplicable && (
          <Button
            type="button"
            label="Exporter"
            icon={DownloadFile}
            variant="ghost"
            onClick={handleDownload}
          />
        )}
      </div>

      <div
        className={cn('grid grid-cols-3 items-center', {
          'opacity-50 pointer-events-none': notApplicable
        })}
      >
        <LabelWithText label="Numéro de DPAE" data={dpaeNumber} />
        <LabelWithText
          label="Date d'export"
          className="place-self-center"
          data={
            <Badge
              className="h-6"
              text={valueRender(dpaeDownloadAt, (d) => DateFormatter.DDMMYYYY(d))}
            />
          }
        />
        <LabelWithText
          label="Date d'import"
          className="place-self-center"
          data={
            <Badge
              className="h-6"
              text={valueRender(dpaeFilledAt, (d) => DateFormatter.DDMMYYYY(d))}
            />
          }
        />
      </div>
    </InnerBox>
  )
}
